import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Divider,
  Grid,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Collapse,
  LinearProgress,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CommentIcon from '@mui/icons-material/Comment';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StadiumIcon from '@mui/icons-material/Stadium';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import config from '../config';
import { jwtDecode } from 'jwt-decode';
import Loading from '../components/Loading';
import { authenticatedFetch } from '../utils/tokenUtils';

// StatComparisonRow Component
const StatComparisonRow = ({ label, homeValue, awayValue, maxValue }) => {
  const homeWidth = (homeValue / maxValue) * 100;
  const awayWidth = (awayValue / maxValue) * 100;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#DAA520', fontWeight: 'bold' }}>
          {homeValue}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: '#000', fontWeight: 'bold' }}>
          {awayValue}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <LinearProgress
            variant="determinate"
            value={homeWidth}
            sx={{
              width: `${homeWidth}%`,
              height: 8,
              borderRadius: '4px 0 0 4px',
              bgcolor: 'rgba(218, 165, 32, 0.2)',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#DAA520',
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={awayWidth}
            sx={{
              width: `${awayWidth}%`,
              height: 8,
              borderRadius: '0 4px 4px 0',
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#000',
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (token) {
    const decoded = jwtDecode(token);
    return decoded.userId;
  }
  return null;
};

const NoTeamsMessage = ({ onAddTeam, isRegistrationAllowed }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      textAlign: 'center',
    }}
  >
    <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>
      No Teams Registered Yet
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      {isRegistrationAllowed
        ? 'Get started by registering your team'
        : 'Team registration is currently closed'}
    </Typography>
    {isRegistrationAllowed && (
      <Button
        variant="contained"
        onClick={onAddTeam}
        sx={{
          bgcolor: '#DAA520',
          color: 'white',
          '&:hover': {
            bgcolor: '#B8860B',
          },
        }}
      >
        Register Team
      </Button>
    )}
  </Box>
);

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [teamName, setTeamName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [players, setPlayers] = useState(['']);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [groupTeams, setGroupTeams] = useState([]);
  const [teamStats, setTeamStats] = useState({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [allMatches, setAllMatches] = useState([]);
  const [allScores, setAllScores] = useState([]);
  const [allScorers, setAllScorers] = useState([]);
  const [expandedContactSection, setExpandedContactSection] = useState(false);
  const [expandedTeamContacts, setExpandedTeamContacts] = useState({});
  const [loading, setLoading] = useState(false);
  const [isRegistrationAllowed, setIsRegistrationAllowed] = useState(true);
  const [teamNameError, setTeamNameError] = useState('');
  const [managerNameError, setManagerNameError] = useState('');
  const [contactNameError, setContactNameError] = useState('');
  const [contactPhoneError, setContactPhoneError] = useState('');
  const [deleteWarningOpen, setDeleteWarningOpen] = useState(false);
  const [saveWarningOpen, setSaveWarningOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const userId = getUserIdFromToken();

  const handleAddPlayer = () => {
    if (players.length < 15) {
      // Maximum 15 players
      setPlayers([...players, '']);
    }
  };

  const handleRemovePlayer = index => {
    const newPlayers = players.filter((_, i) => i !== index);
    setPlayers(newPlayers.length ? newPlayers : ['']); // Always keep at least one field
  };

  const handlePlayerChange = (index, value) => {
    const newPlayers = [...players];
    newPlayers[index] = value;
    setPlayers(newPlayers);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!isRegistrationAllowed) {
      alert('Team registration is currently closed. You cannot add or edit teams at this time.');
      return;
    }

    // Reset previous errors
    setTeamNameError('');
    setManagerNameError('');
    setContactNameError('');
    setContactPhoneError('');

    // Validate required fields
    let hasErrors = false;

    if (!teamName.trim()) {
      setTeamNameError('Team Name is required');
      hasErrors = true;
    }

    if (!contactName.trim()) {
      setContactNameError('Contact Name is required');
      hasErrors = true;
    }

    if (!contactPhone.trim()) {
      setContactPhoneError('Contact Phone is required');
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    setSaveWarningOpen(true);
  };

  const handleSaveConfirm = async () => {
    const method = teamId ? 'PUT' : 'POST';
    const url = teamId ? `${config.apiBaseUrl}/teams/${teamId}` : `${config.apiBaseUrl}/teams`;
    const token = localStorage.getItem('token');
    if (!token) {
      alert('No authentication token found');
      return;
    }
    // response
    const response = await fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        team_name: teamName,
        manager_name: managerName,
        players: players.filter(name => name !== ''),
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail || null,
      }),
    });

    if (response.ok) {
      fetchTeams();
      handleDialogClose();
      setSaveWarningOpen(false);
    } else {
      const errorData = await response.json();
      alert(`Failed to submit data: ${errorData.message || 'Unknown error'}`);
    }
  };

  const handleDialogOpen = () => {
    if (!isRegistrationAllowed) {
      alert('Team registration is currently closed. You cannot add new teams at this time.');
      return;
    }
    setTeamNameError(''); // Clear errors
    setManagerNameError(''); // Clear errors
    setContactNameError(''); // Clear errors
    setContactPhoneError(''); // Clear errors
    setExpandedContactSection(true); // Always expand for mandatory fields
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setTeamId(null);
    setTeamName('');
    setManagerName('');
    setPlayers(['']); // Reset to one empty field
    setContactName('');
    setContactPhone('');
    setContactEmail('');
    setTeamNameError(''); // Clear errors
    setManagerNameError(''); // Clear errors
    setContactNameError(''); // Clear errors
    setContactPhoneError(''); // Clear errors
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const userId = getUserIdFromToken();
      if (!userId) throw new Error('User ID not found in token');
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      const currentYear = new Date().getFullYear();
      const response = await fetch(
        `${config.apiBaseUrl}/users/${userId}/teams?year=${currentYear}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTeam = team => {
    if (!isRegistrationAllowed) {
      alert('Team registration is currently closed. You cannot edit teams at this time.');
      return;
    }
    setTeamId(team.id);
    setTeamName(team.team_name);
    setManagerName(team.manager_name);
    const playerNames = team.players.map(player => player.player_name);
    setPlayers(playerNames.length ? playerNames : ['']);
    setContactName(team.contact_name || '');
    setContactPhone(team.contact_phone || '');
    setContactEmail(team.contact_email || '');
    setTeamNameError(''); // Clear errors
    setManagerNameError(''); // Clear errors
    setContactNameError(''); // Clear errors
    setContactPhoneError(''); // Clear errors
    setExpandedContactSection(true); // Always expand for mandatory fields
    setDialogOpen(true);
  };

  const handleDeleteTeam = teamId => {
    if (!isRegistrationAllowed) {
      alert('Team registration is currently closed. You cannot delete teams at this time.');
      return;
    }
    setTeamToDelete(teamId);
    setDeleteWarningOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!teamToDelete) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No authentication token found');
        return;
      }

      const response = await fetch(`${config.apiBaseUrl}/teams/${teamToDelete}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        fetchTeams(); // Refresh the list
        setDeleteWarningOpen(false);
        setTeamToDelete(null);
      } else {
        const errorData = await response.json();
        console.error('Error deleting team:', errorData);
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('An error occurred while deleting the team.');
    }
  };

  const fetchGroupTeams = async userTeams => {
    // Get all unique groups from user's teams
    const userGroups = [...new Set(userTeams.filter(team => team.group).map(team => team.group))];

    if (userGroups.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      const response = await fetch(`${config.apiBaseUrl}/teams`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const allTeams = await response.json();
        // Get teams from all groups that the user has teams in
        const teamsInUserGroups = allTeams.filter(team => userGroups.includes(team.group));
        setGroupTeams(teamsInUserGroups);
      }
    } catch (error) {
      console.error('Error fetching group teams:', error);
    }
  };

  const calculateTeamStats = async teamId => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      // Fetch all matches
      const matchesResponse = await fetch(`${config.apiBaseUrl}/matches`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const allMatches = await matchesResponse.json();

      // Fetch all scores
      const scoresResponse = await fetch(`${config.apiBaseUrl}/scores`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const allScores = await scoresResponse.json();

      // Filter matches for this team, only including group stage matches
      const teamMatches = allMatches.filter(
        match =>
          (match.home_team_id === teamId || match.away_team_id === teamId) &&
          (!match.type || match.type === 'Group stage')
      );

      let matchesPlayed = 0;
      let wins = 0;
      let draws = 0;
      let losses = 0;
      let goalsFor = 0;
      let goalsAgainst = 0;
      let points = 0;
      let matchHistory = [];

      // Process matches and build match history
      teamMatches.forEach(match => {
        const score = allScores.find(s => s.match_id === match.id);
        if (score) {
          matchesPlayed++;
          const isHomeTeam = match.home_team_id === teamId;
          const teamScore = isHomeTeam ? score.home_team_score : score.away_team_score;
          const opponentScore = isHomeTeam ? score.away_team_score : score.home_team_score;

          goalsFor += teamScore;
          goalsAgainst += opponentScore;

          if (teamScore > opponentScore) {
            wins++;
            points += 3;
          } else if (teamScore === opponentScore) {
            draws++;
            points += 1;
          } else {
            losses++;
          }

          // Add to match history
          matchHistory.push({
            date: match.time,
            isHomeTeam,
            homeTeamId: match.home_team_id,
            awayTeamId: match.away_team_id,
            homeTeamScore: score.home_team_score,
            awayTeamScore: score.away_team_score,
          });
        }
      });

      // Sort matches by date (newest first) and take last 5
      matchHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
      matchHistory = matchHistory.slice(0, 5);

      return {
        matchesPlayed,
        wins,
        draws,
        losses,
        goalsFor,
        goalsAgainst,
        goalDifference: goalsFor - goalsAgainst,
        points,
        matchHistory,
      };
    } catch (error) {
      console.error('Error calculating team stats:', error);
      return {
        matchesPlayed: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
        points: 0,
        matchHistory: [],
      };
    }
  };

  const fetchTeamStats = async () => {
    if (!groupTeams.length) return;

    setIsLoadingStats(true);
    const stats = {};

    for (const team of groupTeams) {
      stats[team.id] = await calculateTeamStats(team.id);
    }

    setTeamStats(stats);
    setIsLoadingStats(false);
  };

  const fetchAllMatchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      // Fetch matches
      const matchesResponse = await fetch(`${config.apiBaseUrl}/matches`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const matches = await matchesResponse.json();
      setAllMatches(matches);

      // Fetch scores
      const scoresResponse = await fetch(`${config.apiBaseUrl}/scores`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const scores = await scoresResponse.json();
      setAllScores(scores);

      // Fetch scorers for all matches
      const scorersPromises = matches.map(match =>
        fetch(`${config.apiBaseUrl}/scorers/match/${match.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).then(res => res.json())
      );
      const scorersResults = await Promise.all(scorersPromises);
      const allScorersData = scorersResults.flat();
      setAllScorers(allScorersData);
    } catch (error) {
      console.error('Error fetching match data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin configs to check if registration is allowed
  const fetchAdminConfigs = async () => {
    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/admin-configs`);
      if (response.ok) {
        const data = await response.json();
        setIsRegistrationAllowed(data.isRegistrationOngoing);
      }
    } catch (error) {
      console.error('Error fetching admin configs:', error);
      // Default to allowing registration if there's an error
      setIsRegistrationAllowed(true);
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchAdminConfigs();
  }, []);

  useEffect(() => {
    const userTeams = teams.filter(team => team.user_id === userId);
    if (userTeams.length > 0) {
      fetchGroupTeams(userTeams);
    }
  }, [teams, userId]);

  useEffect(() => {
    if (groupTeams.length > 0) {
      fetchTeamStats();
    }
  }, [groupTeams]);

  useEffect(() => {
    fetchAllMatchData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleRowClick = teamId => {
    setExpandedTeam(expandedTeam === teamId ? null : teamId);
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderMatchHistory = (teamId, matchHistory) => {
    return (
      <Collapse in={expandedTeam === teamId} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#000000' }}>
            Last 5 Matches
          </Typography>
          {matchHistory.length > 0 ? (
            <Stack spacing={1}>
              {matchHistory.map((match, index) => {
                const teamScore = match.isHomeTeam ? match.homeTeamScore : match.awayTeamScore;
                const opponentScore = match.isHomeTeam ? match.awayTeamScore : match.homeTeamScore;
                const result =
                  teamScore > opponentScore ? 'W' : teamScore < opponentScore ? 'L' : 'D';
                const resultColor =
                  result === 'W' ? '#4CAF50' : result === 'L' ? '#f44336' : '#FFA726';

                // Find if this match is ongoing
                const ongoingMatch = allMatches.find(
                  m =>
                    m.time === match.date &&
                    ((m.home_team_id === match.homeTeamId && m.away_team_id === match.awayTeamId) ||
                      (m.home_team_id === match.awayTeamId &&
                        m.away_team_id === match.homeTeamId)) &&
                    m.ongoing
                );

                // Get current score if match is ongoing
                const currentScore =
                  ongoingMatch && allScores.find(s => s.match_id === ongoingMatch.id);
                const currentTeamScore = currentScore
                  ? match.isHomeTeam
                    ? currentScore.home_team_score
                    : currentScore.away_team_score
                  : null;
                const currentOpponentScore = currentScore
                  ? match.isHomeTeam
                    ? currentScore.away_team_score
                    : currentScore.home_team_score
                  : null;
                const currentResult =
                  currentTeamScore !== null
                    ? currentTeamScore > currentOpponentScore
                      ? '#4CAF50'
                      : currentTeamScore < currentOpponentScore
                        ? '#f44336'
                        : '#757575'
                    : null;

                // Find the opponent team
                const homeTeam = groupTeams.find(t => t.id === match.homeTeamId);
                const awayTeam = groupTeams.find(t => t.id === match.awayTeamId);
                const matchupText = `${homeTeam?.team_name || 'Unknown'} vs ${awayTeam?.team_name || 'Unknown'}`;

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'white',
                    }}
                  >
                    {!ongoingMatch && (
                      <Typography
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: resultColor,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        {result}
                      </Typography>
                    )}
                    {ongoingMatch && (
                      <Chip
                        size="small"
                        label="LIVE"
                        sx={{
                          bgcolor: currentResult,
                          color: 'white',
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                          },
                        }}
                      />
                    )}
                    <Typography variant="body2" sx={{ flex: 1, color: '#000000' }}>
                      {formatDate(match.date)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        flex: 2,
                        textAlign: 'center',
                      }}
                    >
                      {matchupText}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        color: '#000000',
                        flexShrink: 0,
                      }}
                    >
                      {ongoingMatch && currentScore
                        ? `${currentTeamScore}-${currentOpponentScore}`
                        : `${match.homeTeamScore}-${match.awayTeamScore}`}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              No matches played yet
            </Typography>
          )}
        </Box>
      </Collapse>
    );
  };

  const handleToggleContactSection = () => {
    setExpandedContactSection(!expandedContactSection);
  };

  const handleToggleTeamContact = teamId => {
    setExpandedTeamContacts(prev => ({
      ...prev,
      [teamId]: !prev[teamId],
    }));
  };

  const renderMobileView = () => (
    <>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        {isRegistrationAllowed && (
          <Button
            variant="contained"
            onClick={handleDialogOpen}
            sx={{
              bgcolor: '#DAA520',
              color: 'white',
              '&:hover': {
                bgcolor: '#B8860B',
              },
            }}
            startIcon={<AddIcon />}
          >
            Register New Team
          </Button>
        )}
      </Box>
      {teams.length === 0 ? (
        <NoTeamsMessage
          onAddTeam={handleDialogOpen}
          isRegistrationAllowed={isRegistrationAllowed}
        />
      ) : (
        <Stack spacing={2}>
          {teams.map((team, index) => (
            <Card
              key={index}
              sx={{
                width: '100%',
                position: 'relative',
                overflow: 'visible',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: 12,
                  bgcolor: '#DAA520',
                  color: 'white',
                  px: 1.5,
                  py: 0.3,
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'medium',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  zIndex: 1,
                }}
              >
                <PeopleIcon sx={{ fontSize: '0.875rem' }} />
                {team.players?.length || 0} Players
              </Box>

              <CardContent sx={{ pt: 2 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: '#000000',
                  }}
                >
                  {team.team_name}
                </Typography>

                <Stack spacing={1.5}>
                  {team.group && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          color: '#DAA520',
                          background: '#000000',
                          px: 1.25,
                          py: 0.4,
                          borderRadius: '4px 16px 16px 4px',
                          fontWeight: 'medium',
                          boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                          letterSpacing: '0.25px',
                          fontSize: '0.75rem',
                          position: 'relative',
                          border: '1px solid #DAA520',
                          whiteSpace: 'nowrap',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: -8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 4,
                            height: '70%',
                            background: '#DAA520',
                            borderRadius: '2px',
                          },
                        }}
                      >
                        <GroupIcon sx={{ fontSize: '0.875rem', color: '#DAA520' }} />
                        Group {team.group}
                      </Typography>
                    </Box>
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: '0.875rem' }} />
                    Manager: {team.manager_name}
                  </Typography>

                  {/* Contact Person Information - Toggleable */}
                  {team.contact_name && (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 },
                        }}
                        onClick={() => handleToggleTeamContact(team.id)}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <ContactMailIcon sx={{ fontSize: '0.75rem', color: '#DAA520' }} />
                          Contact
                          <IconButton size="small" sx={{ p: 0, ml: -0.5 }} />
                          {expandedTeamContacts[team.id] ? (
                            <ExpandLessIcon sx={{ fontSize: '0.875rem' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: '0.875rem' }} />
                          )}
                        </Typography>
                      </Box>

                      <Collapse in={expandedTeamContacts[team.id] || false}>
                        <Box
                          sx={{
                            pl: 2.3,
                            pt: 0.5,
                            pb: 0.5,
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                            {team.contact_name}
                          </Typography>
                          {team.contact_phone && (
                            <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                              {team.contact_phone}
                            </Typography>
                          )}
                          {team.contact_email && (
                            <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                              {team.contact_email}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: team.registration_fee_paid
                        ? 'rgba(76, 175, 80, 0.1)'
                        : 'rgba(244, 67, 54, 0.1)',
                      px: 1.25,
                      py: 0.4,
                      borderRadius: '12px',
                      width: 'fit-content',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {team.registration_fee_paid ? (
                      <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '0.875rem' }} />
                    ) : (
                      <CancelIcon sx={{ color: '#f44336', fontSize: '0.875rem' }} />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: team.registration_fee_paid ? '#4CAF50' : '#f44336',
                        fontWeight: 'medium',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {team.registration_fee_paid ? 'Fee Paid' : 'Fee Pending'}
                    </Typography>
                  </Box>
                </Stack>

                {/* Payment Information for pending fees */}
                {!team.registration_fee_paid && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      bgcolor: 'rgba(218, 165, 32, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(218, 165, 32, 0.3)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#DAA520',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        mb: 0.5,
                      }}
                    >
                      Pay registration amount to:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#000000',
                        fontSize: '0.7rem',
                        mb: 0.5,
                      }}
                    >
                      Account: FI69 7997 7999 6853 11
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#000000',
                        fontSize: '0.7rem',
                      }}
                    >
                      Recipient: Mirmire Sporting Club Ry
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#000000',
                        fontSize: '0.7rem',
                      }}
                    >
                      Amount: 350 €
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#000000',
                        fontSize: '0.7rem',
                      }}
                    >
                      Message: MAMC entry fee 2026
                    </Typography>
                  </Box>
                )}

                {team.comment && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      bgcolor: 'rgba(218, 165, 32, 0.04)',
                      p: 1.5,
                      borderRadius: 1,
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    <CommentIcon sx={{ color: '#DAA520', fontSize: '1rem' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {team.comment}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#000000',
                      fontWeight: 'bold',
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: '0.875rem',
                    }}
                  >
                    <PeopleIcon sx={{ color: '#DAA520', fontSize: '1rem' }} />
                    Players List
                  </Typography>
                  {team.players && team.players.length > 0 ? (
                    <Grid container spacing={1}>
                      {team.players.map((player, playerIndex) => (
                        <Grid item xs={12} key={playerIndex}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: 'rgba(218, 165, 32, 0.04)',
                            }}
                          >
                            <Typography
                              sx={{
                                bgcolor: '#DAA520',
                                color: 'white',
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 'medium',
                              }}
                            >
                              {playerIndex + 1}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#000000',
                                fontSize: '0.75rem',
                              }}
                            >
                              {player.player_name}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        textAlign: 'center',
                        py: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1,
                        fontSize: '0.75rem',
                      }}
                    >
                      No players registered
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  {isRegistrationAllowed && (
                    <>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditTeam(team)}
                        sx={{
                          flex: 1,
                          color: '#DAA520',
                          borderColor: '#DAA520',
                          '&:hover': {
                            borderColor: '#B8860B',
                            backgroundColor: 'rgba(218, 165, 32, 0.04)',
                          },
                        }}
                        variant="outlined"
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteTeam(team.id)}
                        sx={{
                          flex: 1,
                          color: '#DAA520',
                          borderColor: '#DAA520',
                          '&:hover': {
                            borderColor: '#B8860B',
                            backgroundColor: 'rgba(218, 165, 32, 0.04)',
                          },
                        }}
                        variant="outlined"
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </>
  );

  const renderDesktopView = () => (
    <>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        {isRegistrationAllowed && (
          <Button
            variant="contained"
            onClick={handleDialogOpen}
            sx={{
              bgcolor: '#DAA520',
              color: 'white',
              '&:hover': {
                bgcolor: '#B8860B',
              },
            }}
            startIcon={<AddIcon />}
          >
            Register New Team
          </Button>
        )}
      </Box>
      {teams.length === 0 ? (
        <NoTeamsMessage
          onAddTeam={handleDialogOpen}
          isRegistrationAllowed={isRegistrationAllowed}
        />
      ) : (
        <Grid container spacing={3}>
          {teams.map((team, index) => (
            <Grid item xs={12} key={index}>
              <Card
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  },
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: -12, sm: -15 },
                    right: { xs: 12, sm: 16 },
                    bgcolor: '#DAA520',
                    color: 'white',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.3, sm: 0.5 },
                    borderRadius: '12px',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 'medium',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 1,
                  }}
                >
                  <PeopleIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
                  {team.players?.length || 0} Players
                </Box>

                <CardContent sx={{ flexGrow: 1, pt: { xs: 2, sm: 3 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      mb: 2,
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: '#000000',
                          fontWeight: 'bold',
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: { xs: 1, sm: 2 },
                        }}
                      >
                        {team.team_name}
                      </Typography>
                      <Stack spacing={1}>
                        {team.group && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                color: '#DAA520',
                                background: '#000000',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '4px 16px 16px 4px',
                                fontWeight: 'medium',
                                boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                                letterSpacing: '0.25px',
                                fontSize: '0.6rem',
                                position: 'relative',
                                border: '1px solid #DAA520',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: -8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: 4,
                                  height: '70%',
                                  background: '#DAA520',
                                  borderRadius: '2px',
                                },
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                  width: '30%',
                                  height: '100%',
                                  background:
                                    'linear-gradient(90deg, transparent, rgba(218, 165, 32, 0.1))',
                                  borderRadius: '0 16px 16px 0',
                                },
                              }}
                            >
                              <GroupIcon
                                sx={{
                                  fontSize: '1rem',
                                  color: '#DAA520',
                                }}
                              />
                              Group {team.group}
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <PersonIcon sx={{ fontSize: '1rem' }} />
                          Manager: {team.manager_name}
                        </Typography>

                        {/* Contact Person Information - Toggleable */}
                        {team.contact_name && (
                          <>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                              }}
                              onClick={() => handleToggleTeamContact(team.id)}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <ContactMailIcon sx={{ fontSize: '0.8rem', color: '#DAA520' }} />
                                Contact
                                <IconButton size="small" sx={{ p: 0, ml: -0.5 }}>
                                  {expandedTeamContacts[team.id] ? (
                                    <ExpandLessIcon sx={{ fontSize: '0.875rem' }} />
                                  ) : (
                                    <ExpandMoreIcon sx={{ fontSize: '0.875rem' }} />
                                  )}
                                </IconButton>
                              </Typography>
                            </Box>

                            <Collapse in={expandedTeamContacts[team.id] || false}>
                              <Box sx={{ pl: 2.5, pt: 0.5, pb: 0.5, color: 'text.secondary' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {team.contact_name}
                                </Typography>
                                {team.contact_phone && (
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {team.contact_phone}
                                  </Typography>
                                )}
                                {team.contact_email && (
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {team.contact_email}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </>
                        )}
                      </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: team.registration_fee_paid
                            ? 'rgba(76, 175, 80, 0.1)'
                            : 'rgba(244, 67, 54, 0.1)',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '12px',
                          whiteSpace: 'nowrap',
                          width: 'fit-content',
                        }}
                      >
                        {team.registration_fee_paid ? (
                          <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.1rem' }} />
                        ) : (
                          <CancelIcon sx={{ color: '#f44336', fontSize: '1.1rem' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: team.registration_fee_paid ? '#4CAF50' : '#f44336',
                            fontWeight: 'medium',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {team.registration_fee_paid ? 'Fee Paid' : 'Fee Pending'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Payment Information for pending fees */}
                  {!team.registration_fee_paid && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'rgba(218, 165, 32, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(218, 165, 32, 0.3)',
                        maxWidth: '400px',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#DAA520',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          mb: 1,
                        }}
                      >
                        Pay registration amount in:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#000000',
                          fontSize: '0.8rem',
                          mb: 0.5,
                        }}
                      >
                        Account: FI69 7997 7999 6853 11
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#000000',
                          fontSize: '0.8rem',
                        }}
                      >
                        Recipient: Mirmire Sporting Club Ry
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#000000',
                          fontSize: '0.7rem',
                        }}
                      >
                        Amount: 350 €
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#000000',
                          fontSize: '0.7rem',
                        }}
                      >
                        Message: MAMC entry fee 2026
                      </Typography>
                    </Box>
                  )}

                  {team.comment && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        bgcolor: 'rgba(218, 165, 32, 0.04)',
                        p: 2,
                        borderRadius: 1,
                        mb: 3,
                      }}
                    >
                      <CommentIcon sx={{ color: '#DAA520', fontSize: '1.2rem' }} />
                      <Typography variant="body2" color="text.secondary">
                        {team.comment}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#000000',
                        fontWeight: 'bold',
                        mb: 2,
                        borderBottom: '2px solid #DAA520',
                        pb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PeopleIcon sx={{ color: '#DAA520' }} />
                      Players List
                    </Typography>
                    {team.players && team.players.length > 0 ? (
                      <Grid container spacing={2}>
                        {team.players.map((player, playerIndex) => (
                          <Grid item xs={12} sm={6} md={4} key={playerIndex}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'rgba(218, 165, 32, 0.04)',
                              }}
                            >
                              <Typography
                                sx={{
                                  bgcolor: '#DAA520',
                                  color: 'white',
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.875rem',
                                  fontWeight: 'medium',
                                }}
                              >
                                {playerIndex + 1}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#000000' }}>
                                {player.player_name}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          textAlign: 'center',
                          py: 3,
                          bgcolor: 'rgba(0, 0, 0, 0.02)',
                          borderRadius: 1,
                        }}
                      >
                        No players registered
                      </Typography>
                    )}
                  </Box>
                </CardContent>

                <Divider sx={{ opacity: 0.1 }} />

                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {isRegistrationAllowed && (
                    <>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditTeam(team)}
                        sx={{
                          color: '#DAA520',
                          '&:hover': {
                            bgcolor: 'rgba(218, 165, 32, 0.04)',
                          },
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteTeam(team.id)}
                        sx={{
                          color: '#DAA520',
                          '&:hover': {
                            bgcolor: 'rgba(218, 165, 32, 0.04)',
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );

  const renderGroupStanding = () => {
    const userTeams = teams.filter(team => team.user_id === userId);
    const userGroups = [...new Set(userTeams.filter(team => team.group).map(team => team.group))];

    if (userGroups.length === 0 || groupTeams.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: '#000000' }}>
            Your team is not assigned to any group yet.
          </Typography>
        </Box>
      );
    }

    // Find ongoing matches for teams in this group (only group stage matches)
    const ongoingMatches = allMatches.filter(match => {
      const isGroupMatch = groupTeams.some(
        team => team.id === match.home_team_id || team.id === match.away_team_id
      );
      return match.ongoing && isGroupMatch && (!match.type || match.type === 'Group stage');
    });

    // Render standings for each group
    return (
      <Box sx={{ p: 2 }}>
        {userGroups.map(group => {
          // Filter teams for this group
          const teamsInGroup = groupTeams.filter(team => team.group === group);

          // Sort teams by points, then goal difference, then goals for
          const sortedTeams = [...teamsInGroup].sort((a, b) => {
            const statsA = teamStats[a.id] || { points: 0, goalDifference: 0, goalsFor: 0 };
            const statsB = teamStats[b.id] || { points: 0, goalDifference: 0, goalsFor: 0 };

            if (statsB.points !== statsA.points) {
              return statsB.points - statsA.points;
            }
            if (statsB.goalDifference !== statsA.goalDifference) {
              return statsB.goalDifference - statsA.goalDifference;
            }
            return statsB.goalsFor - statsA.goalsFor;
          });

          return (
            <Box key={group} sx={{ mb: 5 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#000000', mb: 3 }}>
                Group {group} Standing
              </Typography>
              <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                {isLoadingStats ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography>Loading team statistics...</Typography>
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#000000' }}>
                          <TableCell sx={{ color: '#FFFFFF', width: '40px' }}>#</TableCell>
                          <TableCell sx={{ color: '#FFFFFF', minWidth: '200px' }}>Team</TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF' }}>
                            MP
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF' }}>
                            W
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF' }}>
                            D
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF' }}>
                            L
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF' }}>
                            GF
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF' }}>
                            GA
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF' }}>
                            GD
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                            Pts
                          </TableCell>
                          <TableCell sx={{ color: '#FFFFFF', width: '40px' }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedTeams.map((team, index) => {
                          const stats = teamStats[team.id] || {
                            matchesPlayed: 0,
                            wins: 0,
                            draws: 0,
                            losses: 0,
                            goalsFor: 0,
                            goalsAgainst: 0,
                            goalDifference: 0,
                            points: 0,
                            matchHistory: [],
                          };

                          // Find if team has an ongoing match
                          const ongoingMatch = ongoingMatches.find(
                            match =>
                              match.home_team_id === team.id || match.away_team_id === team.id
                          );

                          const ongoingScore =
                            ongoingMatch &&
                            allScores.find(score => score.match_id === ongoingMatch.id);

                          const isHomeTeam = ongoingMatch?.home_team_id === team.id;
                          const opponent = ongoingMatch
                            ? teams.find(
                                t =>
                                  t.id ===
                                  (isHomeTeam
                                    ? ongoingMatch.away_team_id
                                    : ongoingMatch.home_team_id)
                              )
                            : null;

                          return (
                            <React.Fragment key={team.id}>
                              <TableRow
                                hover
                                onClick={() => handleRowClick(team.id)}
                                sx={{
                                  bgcolor:
                                    team.user_id === userId
                                      ? 'rgba(218, 165, 32, 0.1)'
                                      : 'transparent',
                                  '&:hover': {
                                    bgcolor:
                                      team.user_id === userId
                                        ? 'rgba(218, 165, 32, 0.15)'
                                        : 'rgba(0, 0, 0, 0.04)',
                                    cursor: 'pointer',
                                  },
                                }}
                              >
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                      gap: 1,
                                    }}
                                  >
                                    {team.team_name}
                                    {team.user_id === userId && (
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: '0.75rem',
                                          color: '#DAA520',
                                          bgcolor: 'rgba(218, 165, 32, 0.1)',
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 1,
                                        }}
                                      >
                                        Your Team
                                      </Typography>
                                    )}
                                    {ongoingMatch && ongoingScore && (
                                      <Chip
                                        size="small"
                                        label={
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                              component="span"
                                              sx={{
                                                fontSize: '0.65rem',
                                                fontWeight: 'bold',
                                              }}
                                            >
                                              {isHomeTeam
                                                ? `${ongoingScore.home_team_score}-${ongoingScore.away_team_score}`
                                                : `${ongoingScore.away_team_score}-${ongoingScore.home_team_score}`}
                                            </Typography>
                                          </Box>
                                        }
                                        sx={{
                                          bgcolor: (() => {
                                            const teamScore = isHomeTeam
                                              ? ongoingScore.home_team_score
                                              : ongoingScore.away_team_score;
                                            const opponentScore = isHomeTeam
                                              ? ongoingScore.away_team_score
                                              : ongoingScore.home_team_score;

                                            if (teamScore > opponentScore) return '#4CAF50'; // Green for winning
                                            if (teamScore < opponentScore) return '#f44336'; // Red for losing
                                            return '#757575'; // Grey for draw
                                          })(),
                                          color: 'white',
                                          '& .MuiChip-label': {
                                            px: 1,
                                            py: 0.25,
                                          },
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">{stats.matchesPlayed}</TableCell>
                                <TableCell align="center">{stats.wins}</TableCell>
                                <TableCell align="center">{stats.draws}</TableCell>
                                <TableCell align="center">{stats.losses}</TableCell>
                                <TableCell align="center">{stats.goalsFor}</TableCell>
                                <TableCell align="center">{stats.goalsAgainst}</TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    color:
                                      stats.goalDifference > 0
                                        ? '#4CAF50'
                                        : stats.goalDifference < 0
                                          ? '#f44336'
                                          : 'inherit',
                                  }}
                                >
                                  {stats.goalDifference > 0 ? '+' : ''}
                                  {stats.goalDifference}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: '#DAA520',
                                  }}
                                >
                                  {stats.points}
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleRowClick(team.id);
                                    }}
                                  >
                                    {expandedTeam === team.id ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                                  {renderMatchHistory(team.id, stats.matchHistory)}
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Paper>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderTeamMatchHistory = () => {
    const userTeam = teams.find(team => team.user_id === userId);
    if (!userTeam) return null;

    // Find all matches for this team
    const teamMatches = allMatches.filter(
      match =>
        (match.home_team_id === userTeam.id || match.away_team_id === userTeam.id) &&
        (!match.type || match.type === 'Group stage')
    );

    // Sort by date (most recent first)
    teamMatches.sort((a, b) => new Date(b.time) - new Date(a.time));

    // Get scores for these matches
    const teamMatchesWithScores = teamMatches.map(match => {
      const score = allScores.find(s => s.match_id === match.id);
      const isHomeTeam = match.home_team_id === userTeam.id;
      const opponentTeamId = isHomeTeam ? match.away_team_id : match.home_team_id;
      const opponentTeam = teams.find(t => t.id === opponentTeamId);

      // Calculate result
      const teamScore = score ? (isHomeTeam ? score.home_team_score : score.away_team_score) : 0;
      const opponentScore = score
        ? isHomeTeam
          ? score.away_team_score
          : score.home_team_score
        : 0;

      // Get scorers for this match
      const matchScorers = allScorers.filter(scorer => scorer.match_id === match.id);

      // Identify team's scorers
      const teamScorers = matchScorers.filter(scorer => {
        const scorerTeam = teams.find(
          t => t.players && t.players.some(p => p.id === scorer.player_id)
        );
        return scorerTeam && scorerTeam.id === userTeam.id;
      });

      return {
        ...match,
        score,
        isHomeTeam,
        opponentTeam,
        teamScore,
        opponentScore,
        result: teamScore > opponentScore ? 'W' : teamScore < opponentScore ? 'L' : 'D',
        teamScorers,
      };
    });

    if (teamMatchesWithScores.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" sx={{ color: '#000000' }}>
            Your team hasn't played any matches yet.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#000000', mb: 3 }}>
          Match History
        </Typography>
        <Stack spacing={3}>
          {teamMatchesWithScores.map(match => {
            const score = match.score;
            const matchScorers = match.teamScorers;
            const isHomeTeam = match.isHomeTeam;
            const homeTeam = match.homeTeam;
            const awayTeam = match.awayTeam;

            const homeTeamScorers = matchScorers
              .filter(scorer => homeTeam?.players?.some(player => player.id === scorer.player_id))
              .sort((a, b) => a.score_minute - b.score_minute);

            const awayTeamScorers = matchScorers
              .filter(scorer => awayTeam?.players?.some(player => player.id === scorer.player_id))
              .sort((a, b) => a.score_minute - b.score_minute);

            const opponentTeam = isHomeTeam ? awayTeam : homeTeam;
            const opponentScore = isHomeTeam ? score?.away_team_score : score?.home_team_score;
            const teamScore = isHomeTeam ? score?.home_team_score : score?.away_team_score;

            return (
              <Card key={match.id} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#000000',
                        textAlign: 'center',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flex: 1,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: '#DAA520',
                            fontWeight: 'bold',
                          }}
                        >
                          {userTeam.team_name}
                        </Typography>
                      </Box>
                      {score ? (
                        <Typography
                          component="span"
                          sx={{
                            color: '#DAA520',
                            fontWeight: 'bold',
                            px: 2,
                            minWidth: '80px',
                            textAlign: 'center',
                          }}
                        >
                          {teamScore} - {opponentScore}
                        </Typography>
                      ) : (
                        <Typography component="span" sx={{ px: 2 }}>
                          vs
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: '#000000',
                          }}
                        >
                          {opponentTeam?.team_name || 'Unknown Team'}
                        </Typography>
                      </Box>
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AccessTimeIcon sx={{ color: '#DAA520' }} />
                      <Typography variant="body2">
                        {new Date(match.time).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <StadiumIcon sx={{ color: '#DAA520' }} />
                      <Typography variant="body2">Field: {match.field}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SportsSoccerIcon sx={{ color: '#DAA520' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          bgcolor: 'rgba(218, 165, 32, 0.1)',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {match.type || 'Group Stage'}
                      </Typography>
                    </Box>

                    {score && (homeTeamScorers.length > 0 || awayTeamScorers.length > 0) && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#DAA520', mb: 1, fontWeight: 'bold' }}
                        >
                          Scorers
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, color: '#DAA520', fontSize: '0.875rem' }}
                            >
                              {userTeam.team_name}
                            </Typography>
                            <Stack spacing={0.5}>
                              {homeTeamScorers.map((scorer, index) => {
                                const player = scorer.player;
                                return (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      color: '#DAA520',
                                    }}
                                  >
                                    <SportsSoccerIcon sx={{ fontSize: '0.8rem' }} />
                                    {scorer.player?.player_name || 'Unknown Player'} (
                                    {scorer.score_minute}')
                                  </Typography>
                                );
                              })}
                            </Stack>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, color: '#000000', fontSize: '0.875rem' }}
                            >
                              {opponentTeam?.team_name}
                            </Typography>
                            <Stack spacing={0.5}>
                              {awayTeamScorers.map((scorer, index) => {
                                const player = scorer.player;
                                return (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      color: '#000000',
                                    }}
                                  >
                                    <SportsSoccerIcon sx={{ fontSize: '0.8rem' }} />
                                    {scorer.player?.player_name || 'Unknown Player'} (
                                    {scorer.score_minute}')
                                  </Typography>
                                );
                              })}
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Box>
    );
  };

  const renderContent = () => {
    if (selectedTab === 0) {
      return isMobile ? renderMobileView() : renderDesktopView();
    } else if (selectedTab === 1 && showGroupTab) {
      return renderGroupStanding();
    } else if ((selectedTab === 2 && showGroupTab) || (selectedTab === 1 && !showGroupTab)) {
      return renderTeamMatchHistory();
    }
    return null;
  };

  const userTeams = teams.filter(team => team.user_id === userId);
  const showGroupTab = userTeams.some(team => team.group);

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <Loading />
        </Box>
      )}

      <Container
        maxWidth="lg"
        sx={{
          mt: { xs: 2, sm: 3, md: 4 },
          px: { xs: 2, sm: 3 },
          '& > *': {
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: '#000000',
                '&.Mui-selected': {
                  color: '#DAA520',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#DAA520',
              },
            }}
          >
            <Tab label="My Teams" />
            {showGroupTab && <Tab label="Group Standing" />}
            <Tab label="Match History" />
          </Tabs>
        </Box>

        {!isRegistrationAllowed && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'rgba(218, 165, 32, 0.1)',
              borderRadius: 1,
              border: '1px solid #DAA520',
            }}
          >
            <Typography sx={{ color: '#000000', fontWeight: 'medium' }}>
              <strong>Registration is currently closed.</strong> Team information and player lists
              are now locked. Please contact the administrator if you need to make changes.
            </Typography>
          </Box>
        )}

        {renderContent()}

        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>{teamId ? 'Edit Team' : 'Add New Team'}</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Team Name"
                value={teamName}
                onChange={e => {
                  setTeamName(e.target.value);
                  if (teamNameError) setTeamNameError(''); // Clear error when typing
                }}
                required
                error={!!teamNameError}
                helperText={teamNameError}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Manager Name"
                value={managerName}
                onChange={e => {
                  setManagerName(e.target.value);
                  if (managerNameError) setManagerNameError(''); // Clear error when typing
                }}
                error={!!managerNameError}
                helperText={managerNameError}
                sx={{ mb: 3 }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'rgba(33, 150, 243, 0.06)',
                  borderRadius: 1,
                  border: '1px solid rgba(33, 150, 243, 0.15)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1976d2',
                    fontSize: '0.875rem',
                    fontWeight: 'medium',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  Players list can be added later also.
                </Typography>
              </Box>

              <Typography variant="subtitle1" gutterBottom sx={{ color: '#000000' }}>
                Players ({players.length}/15)
              </Typography>

              <Stack spacing={2}>
                {players.map((player, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      label={`Player ${index + 1}`}
                      value={player}
                      onChange={e => handlePlayerChange(index, e.target.value)}
                      required
                    />
                    {players.length > 1 && (
                      <IconButton
                        onClick={() => handleRemovePlayer(index)}
                        sx={{ color: '#DAA520' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                    {index === players.length - 1 && players.length < 15 && (
                      <IconButton
                        onClick={handleAddPlayer}
                        sx={{
                          color: '#DAA520',
                          '&:hover': {
                            bgcolor: 'rgba(218, 165, 32, 0.04)',
                          },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>

              {/* Contact Person Fields - Toggleable */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                  mt: 3,
                }}
                onClick={handleToggleContactSection}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#000000', display: 'flex', alignItems: 'center' }}
                >
                  <ContactMailIcon sx={{ mr: 1 }} />
                  Point of contact *
                </Typography>
                <IconButton size="small">
                  {expandedContactSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedContactSection}>
                <Box sx={{ pl: 1 }}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    value={contactName}
                    onChange={e => {
                      setContactName(e.target.value);
                      if (contactNameError) setContactNameError(''); // Clear error when typing
                    }}
                    required
                    error={!!contactNameError}
                    helperText={contactNameError}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Contact Phone"
                    value={contactPhone}
                    onChange={e => {
                      setContactPhone(e.target.value);
                      if (contactPhoneError) setContactPhoneError(''); // Clear error when typing
                    }}
                    required
                    error={!!contactPhoneError}
                    helperText={contactPhoneError}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Contact Email"
                    value={contactEmail}
                    onChange={e => setContactEmail(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Box>
              </Collapse>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} sx={{ color: '#666666' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              sx={{
                bgcolor: '#DAA520',
                color: 'white',
                '&:hover': {
                  bgcolor: '#B8860B',
                },
              }}
            >
              {teamId ? 'Save Changes' : 'Add Team'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save Warning Dialog */}
        <Dialog
          open={saveWarningOpen}
          onClose={() => setSaveWarningOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Changes</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to save these changes to your team?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveWarningOpen(false)} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveConfirm}
              sx={{
                bgcolor: '#DAA520',
                color: 'white',
                '&:hover': {
                  bgcolor: '#B8860B',
                },
              }}
            >
              Confirm Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Warning Dialog */}
        <Dialog
          open={deleteWarningOpen}
          onClose={() => setDeleteWarningOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Team</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this team? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteWarningOpen(false)} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              sx={{
                bgcolor: '#DAA520',
                color: 'white',
                '&:hover': {
                  bgcolor: '#B8860B',
                },
              }}
            >
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default Dashboard;
